import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import type { MyTask } from '@/api/types';
import { useProjectContext } from '@/project/ProjectContext';
import { ProjectGate, ProjectSelectionHint } from '@/features/projects/ProjectGate';
import { Button } from '@/components/ui/Button';
import { ErrorState, LoadingState, StateMessage } from '@/components/StateMessage/StateMessage';
import activityStyles from '@/features/activities/Activities.module.css';
import styles from './ProjectsPage.module.css';

function isOverdue(dueAt: string | null): boolean {
  if (!dueAt) return false;
  return new Date(dueAt).getTime() < Date.now();
}

export function TasksPage() {
  const { selectedProjectId } = useProjectContext();
  const qc = useQueryClient();

  const tasksQ = useQuery({
    queryKey: ['my-tasks', selectedProjectId],
    queryFn: () => {
      const q = selectedProjectId ? `?project_id=${selectedProjectId}` : '';
      return apiFetch<{ tasks: MyTask[] }>(`/api/activities/mine${q}`);
    },
  });

  const patchTask = useMutation({
    mutationFn: ({ id, ...body }: { id: string; status?: string; snoozed_until?: string }) =>
      apiFetch(`/api/activities/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['my-tasks'] }),
  });

  function snoozeDays(taskId: string, days: number) {
    const until = new Date();
    until.setDate(until.getDate() + days);
    patchTask.mutate({ id: taskId, snoozed_until: until.toISOString() });
  }

  return (
    <ProjectGate pageTitle="Taken">
      <h1 className={styles.title}>Mijn taken</h1>
      <ProjectSelectionHint message="Geen project geselecteerd — taken uit alle projecten waar jij op staat." />
      <StateMessage title="CRM D">
        Openstaande taken toegewezen aan jou (of zonder assignee die jij aanmaakte). Filter via
        project-switcher.
      </StateMessage>

      {tasksQ.isLoading ? (
        <LoadingState />
      ) : tasksQ.isError ? (
        <ErrorState message={(tasksQ.error as Error).message} />
      ) : !tasksQ.data?.tasks.length ? (
        <StateMessage title="Geen open taken">Maak een taak bij een lead of deal.</StateMessage>
      ) : (
        <ul>
          {tasksQ.data.tasks.map((t) => (
            <li key={t.id} className={activityStyles.taskRow}>
              <input
                type="checkbox"
                aria-label="Afgerond"
                onChange={() => patchTask.mutate({ id: t.id, status: 'done' })}
              />
              <div>
                <strong>{t.title ?? t.body ?? 'Taak'}</strong>
                <div className={styles.mutedSmall}>
                  {t.lead_name ? `Lead: ${t.lead_name}` : null}
                  {t.deal_name ? `Deal: ${t.deal_name}` : null}
                </div>
                {t.due_at ? (
                  <div className={isOverdue(t.due_at) ? activityStyles.overdue : styles.mutedSmall}>
                    Vervalt {new Date(t.due_at).toLocaleString('nl-NL')}
                  </div>
                ) : null}
              </div>
              <div className={styles.row}>
                <Button variant="ghost" onClick={() => snoozeDays(t.id, 1)}>
                  +1d
                </Button>
                <Button variant="ghost" onClick={() => snoozeDays(t.id, 7)}>
                  +1w
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </ProjectGate>
  );
}
